import { inject, Injectable } from '@angular/core';
import { ProfileService } from "./profile.service";
import { RoutePath } from "../enums/api.enum";
import { Router } from "@angular/router";
import { INavMenuItem } from "../interfaces/i-nav-menu-item";
import { UiLayoutService } from "../ui-layout/services/ui-layout.service";

/**
 * Service responsible for guarding routes based on user profile permissions and initial page load logic.
 * Handles navigation redirection and selection of navigation menu items according to user access rights.
 * This service is only used when the application is loaded for the first time.
 *
 * @export
 * @class RouteGuardService
 */
@Injectable({
  providedIn: 'root'
})
export class RouteGuardService {
  // The URL initially loaded by the application from the browser address bar.
  loadedUrl = '';
  // Flag to indicate if this is the initial page load.
  isInitialPageLoad = true;

  #profileService = inject(ProfileService);
  #router = inject(Router);
  #uiLayoutService = inject(UiLayoutService);

  /**
   * Guards application routes on initial page load based on the user's profile permissions.
   * Determines the appropriate route to navigate to, sets the active navigation menu item,
   * and redirects the user if they lack access to the requested route.
   *
   * @returns {void}
   */
  routeGuard(): void {
    /*
    NOTE: This method is called every time the user navigates to a new route even though the if statement will be true
    only on the initial page load. The reason for this is to keep the logic centralized in one place.
     */
    if (this.isInitialPageLoad) {
      this.isInitialPageLoad = false;
      const userProfile = this.#profileService.userProfile();
      const userConfig = userProfile?.config;
      let routePath: RoutePath | string = RoutePath.Root;
      const url = this.loadedUrl.split('/')[0];
      const id = this.loadedUrl.split('/')[1];
      const step = this.loadedUrl.split('/')[2];

      // Add any additional cases here to handle specific URLs or conditions
      switch (url) {
        case RoutePath.ManageWorkflows:
        case RoutePath.ManageInstructions:
          routePath = (userConfig?.canManageWorkflows || userConfig?.isAdmin) ? url : RoutePath.Root;
          break;
        case RoutePath.Workflows:
          routePath = (userConfig?.canManageWorkflows || userConfig?.isAdmin || userConfig?.canUseWorkflows || userConfig?.canCreateWorkflows) ? url : RoutePath.Root;
          break;
        case RoutePath.MyWorkflows:
        case RoutePath.MyInstructions:
        case RoutePath.CreateWorkflow:
        case RoutePath.EditWorkflow:
        case RoutePath.CloneWorkflow:
        case RoutePath.CreateInstruction:
        case RoutePath.EditInstruction:
        case RoutePath.CloneInstruction:
          routePath = (userConfig?.canManageWorkflows || userConfig?.isAdmin || userConfig?.canCreateWorkflows) ? url : RoutePath.Root;
          break;
        case RoutePath.CreateAgent:
          routePath = ((userConfig?.canCreateAgents && userConfig?.canManageAgents) || userConfig?.canManageAgents) ? url : RoutePath.Root;
          break;
        case RoutePath.EditAgent:
        case RoutePath.CloneAgent:
          routePath = (userConfig?.canCreateAgents || userConfig?.canManageAgents) ? url : RoutePath.Root;
          break;
        case RoutePath.MyAgents:
          routePath = (userConfig?.canCreateAgents || userConfig?.canManageAgents) ? url : RoutePath.Root;
          break;
        case RoutePath.ManageAgents:
          routePath = (userConfig?.canManageAgents) ? url : RoutePath.Root;
          break;
        case RoutePath.StartConversation:
        case RoutePath.Marketplace:
        case RoutePath.ManageSessions:
          // No additional permission checks beyond authentication; pass through as-is.
          routePath = url;
          break;
        case RoutePath.ManageProfiles:
          routePath = (userConfig?.isAdmin || userConfig?.canManageProfiles) ? url : RoutePath.Root;
          break;
        case RoutePath.ManageKeywords:
          routePath = (userConfig?.isAdmin || userConfig?.canManageKeywords) ? url : RoutePath.Root;
          break;
        case RoutePath.Gsm:
          routePath = (userConfig?.isAdmin) ? url : RoutePath.Root;
          break;
        default:
          break;
      }

      let activeNavItem: INavMenuItem | undefined = this.#uiLayoutService.navItems().find(item => item.id === url);

      if (activeNavItem) {
        this.#uiLayoutService.setSelectedNavItem(activeNavItem?.id!);
      }

      // Append any path segments (id, step) that were present in the loaded URL.
      const segments = [id, step].filter(Boolean);
      if (segments.length) {
        routePath = `${routePath}/${segments.join('/')}`;
      }

      this.#router.navigateByUrl(routePath ? `/${routePath}` : '/');
    }
  }
}
